import { Popover } from "antd";
import React, { useEffect, useRef, useState } from "react";
import useGetQueueConfig from "../../apis/tryst/get-queue-config";
import Tag from "../../atoms/chip/tag";
import {
  AptTagsPanelProps,
  TagFormPropsType,
  TagRecord,
  TagRecordWithCount,
} from "../../types/appointment";
import { track } from "../../utils/tracking";
import CreateTagsForm from "./create-tag-form";
import AptTagsPanel from "./tags-panel";

export default function MinimizedTags({
  filteredTags,
  allTagIds,
  aid,
  onTagCreation,
  onTagClick,
}: {
  filteredTags: Set<TagRecord["tag_id"]>;
  allTagIds: TagRecord["tag_id"][];
  aid: string;
  onTagCreation: TagFormPropsType["onAdditionOfTag"];
  onTagClick: AptTagsPanelProps["onTagClick"];
}) {
  const { data: queueConfigData } = useGetQueueConfig();

  const { data } = queueConfigData || {};
  const { tags: allTags = [] } = data || {};
  const [
    { showAllTagsSelector, showTagForm, noOfTagsToShow, widthOfEachTag },
    setState,
  ] = useState({
    showAllTagsSelector: false,
    showTagForm: false,
    noOfTagsToShow: 0,
    widthOfEachTag: 75,
  });

  const tagContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (tagContainerRef.current?.clientWidth) {
      const availableWidthForTags = tagContainerRef.current?.clientWidth - 40;
      let tagWidth = 75;
      const noOfTagsPossibleToShowWithoutConstraints = Math.floor(
        availableWidthForTags / 75
      );
      if (noOfTagsPossibleToShowWithoutConstraints === 0) {
        tagWidth = availableWidthForTags;
      }
      const noOfTagsPossibleToShow = Math.max(
        1,
        noOfTagsPossibleToShowWithoutConstraints
      );

      setState((prev) => ({
        ...prev,
        noOfTagsToShow: noOfTagsPossibleToShow,
        widthOfEachTag: tagWidth,
      }));
    }
  }, [tagContainerRef]);

  const tagsMap = new Map<TagRecord["tag_id"], TagRecordWithCount>();

  for (const tag of allTags) {
    const { tag_id } = tag;
    tagsMap.set(tag_id, tag);
  }

  const tagsInSortedOrder = [...allTagIds].sort((l, r) => {
    let score = 0;
    if (filteredTags.has(l)) {
      score -= 10;
    } else if (filteredTags.has(r)) {
      score += 10;
    }
    const tagOnLeft = tagsMap.get(l);
    const tagonRight = tagsMap.get(r);

    if (tagOnLeft && tagonRight) {
      score += tagOnLeft?.label.localeCompare(tagonRight?.label);
    }

    return score;
  });

  const tagsToRender = tagsInSortedOrder.filter(
    (tagData, idx) => idx < noOfTagsToShow
  );

  const noOfTagsHidden = tagsInSortedOrder.length - noOfTagsToShow;

  function closeForm() {
    setState((prev) => ({ ...prev, showTagForm: false }));
  }

  function closePopover() {
    setState((prev) => ({ ...prev, showAllTagsSelector: false }));
  }

  const tooltipContent = (
    <div className={"px-12 py-11 bg-white flex space-x-8"}>
      {tagsInSortedOrder.map((tagId) => {
        const currentTag = tagsMap.get(tagId);

        if (!currentTag) {
          return null;
        }

        return (
          <Tag
            key={tagId}
            text={currentTag.label}
            textColor={currentTag.hex}
            backgroundColor={currentTag.bg_hex}
          />
        );
      })}
    </div>
  );

  const [showSelectedTagPopover, setShowSelectedTagPopover] =
    React.useState<boolean>(false);

  return (
    <Popover
      trigger={"click"}
      visible={showAllTagsSelector}
      onVisibleChange={(visibility) => {
        track("BILLING_TAGS_FORM_POPOVER_VIEW", {
          visibility: `${visibility}`,
        });
        if (visibility) {
          setState((prev) => ({
            ...prev,
            showAllTagsSelector: visibility,
            showTagForm: false,
          }));
          setShowSelectedTagPopover(false);
        } else {
          setState((prev) => ({
            ...prev,
            showAllTagsSelector: visibility,
          }));
        }
      }}
      overlayClassName={"popover-p-0 popover-rounded-16"}
      placement={"leftTop"}
      overlayInnerStyle={{ borderRadius: "16px" }}
      destroyTooltipOnHide={true}
      content={
        <TagPopoverContent
          showTagForm={showTagForm}
          closeForm={closeForm}
          aid={aid}
          onTagCreation={onTagCreation}
          onTagClick={onTagClick}
          onAddNewClick={() => {
            setState((prev) => ({ ...prev, showTagForm: true }));
          }}
          allTags={allTags}
          allTagIds={allTagIds}
          filteredTags={filteredTags}
          closePopover={closePopover}
        />
      }
    >
      <div
        ref={tagContainerRef}
        className="appt-tags cursor-pointer space-x-5 hide-scrollbar"
      >
        {allTagIds.length === 0 ? (
          <Tag
            text="+ Add a Tag"
            textColor="#004BA8"
            backgroundColor="#F4F6F9"
          />
        ) : (
          <Popover
            overlayClassName="popover-p-0 popover-rounded-16"
            placement="top"
            content={tooltipContent}
            visible={showSelectedTagPopover}
            onVisibleChange={(visibility) => {
              if (visibility && !showAllTagsSelector) {
                setShowSelectedTagPopover(true);
              }
              if (!visibility) {
                setShowSelectedTagPopover(false);
              }
              track("TAGS_OVERVIEW_POPOVER_VIEW", {
                visibility: `${visibility}`,
              });
            }}
            overlayInnerStyle={{ borderRadius: "16px" }}
          >
            <div className="flex space-x-5">
              {tagsToRender.map((tagId) => {
                const currentTag = tagsMap.get(tagId);

                if (!currentTag) {
                  return null;
                }

                return (
                  <Tag
                    key={currentTag.tag_id}
                    text={currentTag.label}
                    textColor={currentTag.hex}
                    backgroundColor={currentTag.bg_hex}
                    additionalCssProps={{
                      maxWidth: `${widthOfEachTag}px`,
                    }}
                  />
                );
              })}

              {noOfTagsHidden > 0 ? (
                <div
                  className={`flex flex-wrap max-w-360 items-center px-8 py-4 font-700 rounded-8 bg-darwin-primary-200`}
                >
                  <p className="font-700 uppercase text-10 truncate text-darwin-primary-500">{`+ ${noOfTagsHidden}`}</p>
                </div>
              ) : null}
            </div>
          </Popover>
        )}
      </div>
    </Popover>
  );
}

function TagPopoverContent({
  showTagForm,
  closeForm,
  onTagCreation,
  aid,
  allTags,
  filteredTags,
  allTagIds,
  onTagClick,
  onAddNewClick,
  closePopover,
}: {
  showTagForm: boolean;
  closeForm: () => void;
  onTagCreation: TagFormPropsType["onAdditionOfTag"];
  aid: string;
  allTags: TagRecord[];
  filteredTags: Set<TagRecord["tag_id"]>;
  allTagIds: TagRecord["tag_id"][];
  onTagClick: AptTagsPanelProps["onTagClick"];
  onAddNewClick: () => void;
  closePopover: () => void;
}) {
  useEffect(() => {
    function closePopoverOnEsc(e: KeyboardEvent) {
      if (e.key === "Escape") {
        closePopover();
      }
    }
    document.addEventListener("keydown", closePopoverOnEsc);
    return () => {
      document.removeEventListener("keydown", closePopoverOnEsc);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (showTagForm) {
    return (
      <CreateTagsForm
        closeForm={closeForm}
        onAdditionOfTag={onTagCreation}
        aid={aid}
      />
    );
  }

  return (
    <AptTagsPanel
      allAppointmentTags={allTags}
      selectedTags={filteredTags}
      taggedTagIds={allTagIds}
      aid={aid}
      onTagClick={onTagClick}
      onAddNewClick={onAddNewClick}
    />
  );
}
